import { useMemo } from "react";
import { Plus, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ColorPicker } from "@/components/ui/color-picker";
import { TailwindColorSelectorPopover } from "@/components/editor/themes/components/tailwind-color-selector-popover";

import { CSSProperty } from "@/types/property-panel.types";
import PropertySection from "../PropertySection";
import {
  cssPropertyOptions,
  propertiesWithPx,
  colorProperties,
  cssValueSuggestions,
} from "@/lib/visual-editor/helper";

interface CSSPropertiesSectionProps {
  cssProperties: CSSProperty[];
  onAddNew: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: "property" | "value", value: string) => void;
  onClearAll: () => void;
}

export const CSSPropertiesSection = ({
  cssProperties,
  onAddNew,
  onRemove,
  onUpdate,
  onClearAll,
}: CSSPropertiesSectionProps) => {
  const usedProperties = useMemo(
    () => new Set(cssProperties.map((p) => p.property).filter(Boolean)),
    [cssProperties],
  );

  /** Render the value input based on property type */
  const renderValueInput = (cssProperty: CSSProperty) => {
    const prop = cssProperty.property;

    // Color properties
    if (colorProperties.includes(prop)) {
      return (
        <div className="flex items-center gap-2">
          <ColorPicker
            value={cssProperty.value}
            onChange={(value) => onUpdate(cssProperty.id, "value", value)}
            className="flex-shrink-0 h-8 w-8 border-border"
          />
          <TailwindColorSelectorPopover
            value={cssProperty.value}
            onSelect={(color) => onUpdate(cssProperty.id, "value", color)}
          />
          <Input
            type="text"
            value={cssProperty.value}
            onChange={(e) => onUpdate(cssProperty.id, "value", e.target.value)}
            className="h-8 bg-background border-border text-foreground text-xs font-mono"
            placeholder="#000000"
          />
        </div>
      );
    }

    // Enum properties
    const suggestions = cssValueSuggestions[prop];
    if (suggestions) {
      return (
        <Select
          value={cssProperty.value}
          onValueChange={(value) => onUpdate(cssProperty.id, "value", value)}
        >
          <SelectTrigger className="w-full bg-background border border-border rounded-md text-foreground h-8 text-xs">
            <SelectValue placeholder="Select value" />
          </SelectTrigger>
          <SelectContent>
            {suggestions.map((val) => (
              <SelectItem key={val} value={val} className="text-xs">
                {val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Default text input
    return (
      <Input
        type="text"
        value={cssProperty.value}
        onChange={(e) => onUpdate(cssProperty.id, "value", e.target.value)}
        className="h-8 bg-background border-border text-foreground text-xs"
        placeholder={
          propertiesWithPx.includes(prop) ? "0 px" : "Enter value..."
        }
      />
    );
  };

  return (
    <PropertySection
      title="CSS Properties"
      action={
        <Hint text="Add property" delayDuration={0}>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={onAddNew}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </Hint>
      }
    >
      {/* Property list */}
      <div className="flex flex-col gap-3">
        {cssProperties.map((cssProperty) => (
          <div key={cssProperty.id} className="flex flex-col gap-1.5">
            {/* Property selector + remove */}
            <div className="flex items-center justify-between">
              <Select
                onValueChange={(value: string) =>
                  onUpdate(cssProperty.id, "property", value)
                }
                value={cssProperty.property}
              >
                <SelectTrigger className="h-7 w-auto min-w-0 border-none shadow-none bg-transparent px-0 text-xs text-muted-foreground hover:text-foreground font-medium gap-1 cursor-pointer">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {cssPropertyOptions
                    .filter(
                      (opt) =>
                        !usedProperties.has(opt.value) ||
                        opt.value === cssProperty.property,
                    )
                    .map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        className="text-xs"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Hint text="Remove" delayDuration={0}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 cursor-pointer text-muted-foreground/40 hover:text-destructive"
                  onClick={() => onRemove(cssProperty.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Hint>
            </div>

            {/* Value input */}
            {renderValueInput(cssProperty)}
          </div>
        ))}
      </div>

      {/* Clear all */}
      {cssProperties.length > 1 && (
        <button
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          Clear all
        </button>
      )}

      {/* Empty state */}
      {cssProperties.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-2">
          Add custom inline style overrides
        </p>
      )}
    </PropertySection>
  );
};
